import { useMemo } from "react";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useGlobalManualLocationOrder } from "../contexts/Staffing/GlobalManualLocationOrderContext";
import { useUserSettingsContext } from "../contexts/Staffing/UserSettingsContext";
import { DEFAULT_HPPD_INCLUDE_TRAINEES, DEFAULT_SHOW_ADMINS } from "../contexts/Staffing/UserSettingsProvider";
import type { BuildAiPrintoutDataInput } from "../downloads/aiPrintout/buildAiPrintoutData";
import { useRevealWage } from "./useRevealWage";

export type AiPrintoutViewerInputs = Pick<
	BuildAiPrintoutDataInput,
	| "isSnf"
	| "rolesToDisplay"
	| "findDepartmentLocation"
	| "customLocationOrder"
	| "hppdIncludeTrainees"
	| "customTimeGroups"
	| "showAdmins"
	| "includeTimeOffReason"
	| "censusGrouping"
	| "wagesVisible"
>;

/**
 * What a custom printout takes from the viewer and the department rather than from the day: the printout
 * settings the Detailed and Summary printouts read, the census grouping and location order the Day view uses,
 * and whether cost may show — as the Day view's header decides it: never for a hospital department, and only
 * while the viewer's wages are showing.
 */
export const useAiPrintoutViewerInputs = (isoDate: string): AiPrintoutViewerInputs => {
	const { isHospital, departmentNurseTypes, wageData, findDepartmentLocationForNurse } = useHospitalData();
	const { settings } = useUserSettingsContext();
	const canViewWages = useRevealWage();
	const { customStringOrderMap } = useGlobalManualLocationOrder();
	return useMemo(() => {
		const showsWages = !isHospital && canViewWages;
		return {
			isSnf: !isHospital,
			rolesToDisplay: departmentNurseTypes,
			findDepartmentLocation: findDepartmentLocationForNurse,
			customLocationOrder: customStringOrderMap,
			// DEFAULT: the same fallbacks the Detailed and Summary printouts (`usePrintout`) apply to a viewer who never set these.
			hppdIncludeTrainees: settings.hppdIncludeTrainees ?? DEFAULT_HPPD_INCLUDE_TRAINEES,
			customTimeGroups: settings.customTimeGroups,
			showAdmins: settings.showAdmins ?? DEFAULT_SHOW_ADMINS,
			includeTimeOffReason: settings.includeTimeOffReason ?? true,
			censusGrouping: settings.censusByLocation,
			// As `calculateDayStats` looks wages up: by the day's date, in the side's own map.
			wagesVisible: {
				scheduled: showsWages && wageData?.dayWageStats[isoDate] !== undefined,
				actual: showsWages && wageData?.dayWageStatsActual[isoDate] !== undefined,
			},
		};
	}, [isHospital, canViewWages, departmentNurseTypes, findDepartmentLocationForNurse, customStringOrderMap, settings, wageData, isoDate]);
};
